import { TenantConfig} from '../types/types';

// ConfigService - Servicio para gestionar configuración multi-tenant
//Este servicio detecta el dominio actual y carga la configuración apropiada
class ConfigService {
  private config: TenantConfig | null = null;
  private initialized: boolean = false;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const hostname = this.getCurrentHostname();

      this.config = await this.loadTenantConfig(hostname);

      this.initialized = true;
    } catch (error) {
      console.error('Error initializing config service:', error);
      this.config = this.getDefaultConfig();
      this.initialized = true;
    }
  }


   /// Obtiene el hostname actual (con soporte para desarrollo local)
  private getCurrentHostname(): string {
    if (typeof window !== 'undefined') {
      return window.location.hostname + (window.location.port ? `:${window.location.port}` : '');
    }
    return 'localhost:3000';
  }


  // Carga la configuración específica del tenant
  private async loadTenantConfig(tenantId: string): Promise<TenantConfig> {
    try {
      const hostname = window.location.hostname;
      const apiUrl = 'https://nodales.bike-in.com';
      const response = await fetch(`${apiUrl}/api/tenant/${tenantId}`);
      if (!response.ok) {
        throw new Error(`Failed to load config for tenant ${tenantId}: ${response.statusText}`);
      }
      const config = await response.json();
  
      if (!config.tenantId || !config.apiBaseUrl || !config.osrmUrl) {
        throw new Error(`Invalid configuration for tenant ${tenantId}: missing required fields`);
      }
  
      return config;
    } catch (error) {
      console.error(`Error loading tenant config for ${tenantId}:`, error);
      throw error;
    }
  }

  //Configuración por defecto (fallback)
  private getDefaultConfig(): TenantConfig {
    return {
      tenantId: 'enkartur',
      name: 'enkartur',
      displayName: 'Enkartur',
      apiBaseUrl: 'https://enkartur-ea7a8c30fabc.backend.ridespark.com/',
      osrmUrl: 'https://enkartur-ea7a8c30fabc.backend.ridespark.com',
      useMockData: true,
      mapCenter: [43.3623, -8.4115],
      defaultZoom: 10,
      theme: {
        primaryColor: '#1976d2'
      },
      features: {
        enableRoutes: true,
        enableNodePoints: true,
        enablePdfExport: true
      }
    };
  }

  /**
   * Obtiene la configuración actual (debe llamarse después de initialize)
   */
  getConfig(): TenantConfig {
    if (!this.initialized || !this.config) {
      console.warn('ConfigService not initialized, returning default config');
      return this.getDefaultConfig();
    }
    return this.config;
  }

  /**
   * Helper: Obtiene el API base URL
   */
  getApiBaseUrl(): string {
    return this.getConfig().apiBaseUrl;
  }

  /**
   * Helper: Obtiene el OSRM URL
   */
  getOsrmUrl(): string {
    return this.getConfig().osrmUrl;
  }

  /**
   * Helper: Verifica si se debe usar datos mock
   */
  shouldUseMockData(): boolean {
    return this.getConfig().useMockData ?? true;
  }

  /**
   * Helper: Obtiene el centro del mapa
   */
  getMapCenter(): [number, number] {
    return this.getConfig().mapCenter ?? [43.3623, -8.4115];
  }

  /**
   * Helper: Obtiene el zoom por defecto
   */
  getDefaultZoom(): number {
    return this.getConfig().defaultZoom ?? 10;
  }

  /**
   * Helper: Verifica si los puntos nodales están habilitados
   */
  isNodePointsEnabled(): boolean {
    return this.getConfig().features?.enableNodePoints ?? true;
  }

  /**
   * Verifica si el servicio está inicializado
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Exportar una instancia singleton
const configService = new ConfigService();
export default configService;
